import axios from "axios";
import {
  ASKFORPRINT,
  CART_INITIAL_STATE,
  GETSINGLEFULLCONFIGS,
  getPreUrlAccordingToCart
} from "../../constants/constant";

const DEFAULT_AGENT_PORT = 17312;
const encoder = new TextEncoder();
const configCache = new Map();
const connections = { ble: null, serial: null, usb: null };

const PROFILE_COLUMNS = {
  "58mm-384": 32,
  "58mm-432": 36,
  "76mm-512": 42,
  "80mm-512": 42,
  "80mm-576": 48,
  "112mm-832": 69,
};

const concatBytes = (...parts) => {
  const valid = parts.filter(Boolean);
  const output = new Uint8Array(valid.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  valid.forEach((part) => { output.set(part, offset); offset += part.length; });
  return output;
};

const toBase64 = (bytes) => {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, Math.min(offset + 0x8000, bytes.length)));
  }
  return btoa(binary);
};

const stripMarkup = (value) => String(value || "")
  .replace(/<img>.*?<\/img>/gi, "")
  .replace(/<\/?(?:b|u|lang-hi)>/gi, "")
  .replace(/<\/?font(?:\s+[^>]*)?>/gi, "")
  .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

const fit = (text, width) => {
  const value = stripMarkup(text);
  return value.length > width ? value.slice(0, width) : value;
};

const formatTaggedLine = (line, columns) => {
  const matches = [...String(line || "").matchAll(/\[([LCR])\]([^\[]*)/g)];
  if (!matches.length) return fit(line, columns);
  const segments = matches.map((match) => ({ align: match[1], text: stripMarkup(match[2]) }));
  if (segments.length === 1) {
    const text = fit(segments[0].text, columns);
    if (segments[0].align === "C") return text.padStart(Math.floor((columns + text.length) / 2));
    if (segments[0].align === "R") return text.padStart(columns);
    return text;
  }
  if (segments.length === 2 && segments[0].align === "L" && segments[1].align === "R") {
    const right = fit(segments[1].text, Math.floor(columns / 2));
    const left = fit(segments[0].text, columns - right.length - 1);
    return `${left}${" ".repeat(Math.max(1, columns - left.length - right.length))}${right}`;
  }
  const cellWidth = Math.floor(columns / segments.length);
  return segments.map((segment, index) => {
    const width = index === segments.length - 1 ? columns - (cellWidth * index) : cellWidth;
    const text = fit(segment.text, width);
    if (segment.align === "R") return text.padStart(width);
    if (segment.align === "C") return text.padStart(Math.floor((width + text.length) / 2)).padEnd(width);
    return text.padEnd(width);
  }).join("").trimEnd();
};

export const buildTaggedEscPos = (taggedText, channelConfig) => {
  const columns = PROFILE_COLUMNS[channelConfig.profile]
    || Math.max(24, Math.round((Number(channelConfig.paperWidthMm) || 80) * 0.6));
  const output = [new Uint8Array([0x1b, 0x40])];
  String(taggedText || "").replace(/\r\n/g, "\n").split("\n").forEach((line) => {
    const bold = /<b>/i.test(line);
    const underline = /<u>/i.test(line);
    const size = /size=['"](big(?:-[2-6])?|wide|tall)['"]/i.exec(line)?.[1] || "normal";
    const sizeByte = size === "wide" ? 0x10 : size === "tall" ? 0x01 : size === "normal" ? 0x00 : 0x11;
    output.push(new Uint8Array([0x1b, 0x45, bold ? 1 : 0]));
    output.push(new Uint8Array([0x1b, 0x2d, underline ? 1 : 0]));
    output.push(new Uint8Array([0x1d, 0x21, sizeByte]));
    output.push(encoder.encode(`${formatTaggedLine(line, columns)}\n`));
  });
  output.push(new Uint8Array([0x1b, 0x45, 0, 0x1b, 0x2d, 0, 0x1d, 0x21, 0, 0x1b, 0x64, 4]));
  if (channelConfig.cutMode !== "none") {
    output.push(new Uint8Array([0x1d, 0x56, channelConfig.cutMode === "full" ? 0 : 1]));
  }
  return concatBytes(...output);
};

const requestId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;

const normalizeAgentPort = (value) => {
  const port = Number(value) || DEFAULT_AGENT_PORT;
  if (!Number.isInteger(port) || port < 1 || port > 65535) throw new Error("Printer Agent port must be between 1 and 65535.");
  return port;
};

const agentRequest = async (agentPort, path, body, pair = false) => {
  const port = normalizeAgentPort(agentPort);
  try {
    const response = await fetch(`http://127.0.0.1:${port}${path}`, {
      method: body ? "POST" : "GET",
      cache: "no-store",
      targetAddressSpace: "loopback",
      headers: {
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...(pair ? { "X-SNP-Pair": "confirm" } : {}),
        "X-Request-ID": requestId(),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || `Printer Agent HTTP ${response.status}`);
    return payload;
  } catch (error) {
    if (error instanceof TypeError || /failed to fetch/i.test(error?.message || "")) {
      throw new Error(`Cannot reach SNP Printer Agent on localhost:${port}. Download and start the Agent, confirm its port, and allow Apps on device in the browser.`);
    }
    throw error;
  }
};

const ensureAgent = async (agentPort) => {
  const health = await agentRequest(agentPort, "/health");
  if (!health.paired) await agentRequest(agentPort, "/pair", {}, true);
};

const parseHex = (value, label) => {
  const normalized = String(value || "").trim().replace(/^0x/i, "");
  if (!/^[0-9a-f]+$/i.test(normalized)) throw new Error(`${label} is invalid.`);
  return Number.parseInt(normalized, 16);
};

const connectBle = async (config, allowPrompt = true) => {
  if (!window.isSecureContext || !navigator.bluetooth) throw new Error("Web Bluetooth is unavailable.");
  const granted = navigator.bluetooth.getDevices ? await navigator.bluetooth.getDevices() : [];
  const device = granted[0] || (allowPrompt ? await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: [config.serviceUuid],
  }) : null);
  if (!device) throw new Error("BLE printer is not paired. Connect it from Printer Configuration first.");
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(config.serviceUuid);
  connections.ble = { device, server, characteristic: await service.getCharacteristic(config.characteristicUuid) };
};

const connectSerial = async (config, allowPrompt = true) => {
  if (!window.isSecureContext || !navigator.serial) throw new Error("Web Serial is unavailable.");
  const ports = await navigator.serial.getPorts();
  const port = ports[0] || (allowPrompt ? await navigator.serial.requestPort() : null);
  if (!port) throw new Error("Serial printer is not paired. Connect it from Printer Configuration first.");
  if (!port.writable) await port.open({ baudRate: Number(config.baudRate) || 9600, bufferSize: 65536 });
  connections.serial = port;
};

const findUsbOutput = (device) => {
  for (const iface of device.configuration?.interfaces || []) {
    for (const alternate of iface.alternates || []) {
      const endpoint = alternate.endpoints?.find((candidate) => candidate.direction === "out");
      if (endpoint) return { interfaceNumber: iface.interfaceNumber, alternateSetting: alternate.alternateSetting, endpointNumber: endpoint.endpointNumber };
    }
  }
  return null;
};

const connectUsb = async (config, allowPrompt = true) => {
  if (!window.isSecureContext || !navigator.usb) throw new Error("WebUSB is unavailable.");
  const filter = { vendorId: parseHex(config.vendorId, "USB Vendor ID"), productId: parseHex(config.productId, "USB Product ID") };
  const granted = await navigator.usb.getDevices();
  const device = granted.find((item) => item.vendorId === filter.vendorId && item.productId === filter.productId)
    || (allowPrompt ? await navigator.usb.requestDevice({ filters: [filter] }) : null);
  if (!device) throw new Error("USB printer is not paired. Connect it from Printer Configuration first.");
  if (!device.opened) await device.open();
  if (!device.configuration) await device.selectConfiguration(device.configurations?.[0]?.configurationValue || 1);
  const output = findUsbOutput(device);
  if (!output) throw new Error("No writable USB endpoint was found.");
  await device.claimInterface(output.interfaceNumber);
  if (output.alternateSetting) await device.selectAlternateInterface(output.interfaceNumber, output.alternateSetting);
  connections.usb = { device, ...output };
};

const sendDirect = async (type, bytes, config) => {
  if (type === "ble") {
    if (!connections.ble?.server?.connected) await connectBle(config, false);
    const characteristic = connections.ble.characteristic;
    for (let offset = 0; offset < bytes.length; offset += 180) {
      const chunk = bytes.slice(offset, offset + 180);
      if (characteristic.writeValueWithoutResponse) await characteristic.writeValueWithoutResponse(chunk);
      else await characteristic.writeValue(chunk);
    }
    return;
  }
  if (type === "serial") {
    if (!connections.serial?.writable) await connectSerial(config, false);
    const writer = connections.serial.writable.getWriter();
    try { await writer.write(bytes); } finally { writer.releaseLock(); }
    return;
  }
  if (type === "usb") {
    if (!connections.usb?.device?.opened) await connectUsb(config, false);
    for (let offset = 0; offset < bytes.length; offset += 16384) {
      const result = await connections.usb.device.transferOut(connections.usb.endpointNumber, bytes.slice(offset, offset + 16384));
      if (result.status !== "ok") throw new Error(`USB transfer failed: ${result.status}`);
    }
  }
};

const sendEscPos = async (type, bytes, config) => {
  if (type === "tcp" || type === "windows") {
    await ensureAgent(config.agentPort);
    const body = type === "tcp"
      ? { host: config.ip, port: Number(config.port) || 9100, timeoutMs: 5000, dataBase64: toBase64(bytes) }
      : { printerName: config.printerName, documentName: "SNP Billing ESC/POS", dataBase64: toBase64(bytes) };
    await agentRequest(config.agentPort, type === "tcp" ? "/api/tcp/send" : "/api/windows/send", body);
    return;
  }
  await sendDirect(type, bytes, config);
};

const loadConfig = async (baseUrl, shopHash) => {
  const key = `${baseUrl}|${shopHash}`;
  const cached = configCache.get(key);
  if (cached && Date.now() - cached.loadedAt < 60_000) return cached.value;
  const prefix = getPreUrlAccordingToCart(CART_INITIAL_STATE.cartType);
  const url = `${baseUrl}${prefix}${GETSINGLEFULLCONFIGS}?adminId=${encodeURIComponent(CART_INITIAL_STATE.apiToken)}&configName=${encodeURIComponent(ASKFORPRINT)}&shopHash=${encodeURIComponent(shopHash)}`;
  const { data } = await axios.get(url);
  const value = data?.jcJson || {};
  configCache.set(key, { value, loadedAt: Date.now() });
  return value;
};

const channelConfig = (map, channel) => ({
  enabled: map[`${channel}_enabled`] !== false,
  type: String(map[`${channel}_type`] || "tcp").toLowerCase(),
  profile: map[`${channel}_profile`] || "80mm-576",
  paperWidthMm: map[`${channel}_paper_width_mm`],
  cutMode: map[`${channel}_cut_mode`] || "partial",
  copies: Math.max(1, Math.min(10, Number(map[`${channel}_copies`]) || 1)),
  ip: map[`${channel}_ip`],
  port: map[`${channel}_port`],
  printerName: map[`${channel}_printer_name`],
  vendorId: map[`${channel}_vendor_id`],
  productId: map[`${channel}_product_id`],
  serviceUuid: map[`${channel}_service_uuid`],
  characteristicUuid: map[`${channel}_characteristic_uuid`],
  baudRate: map[`${channel}_baud_rate`],
  agentPort: normalizeAgentPort(map.silent_agent_port),
});

export const prepareSilentPrinter = async (map, channel) => {
  const config = channelConfig(map || {}, channel);
  if (!config.enabled) throw new Error("Enable this printer channel first.");
  if (config.type === "tcp" || config.type === "windows") {
    await ensureAgent(config.agentPort);
    if (config.type === "tcp") {
      await agentRequest(config.agentPort, "/api/tcp/check", { host: config.ip, port: Number(config.port) || 9100, timeoutMs: 5000 });
    } else {
      await agentRequest(config.agentPort, "/api/windows/check", { printerName: config.printerName, documentName: "SNP Billing ESC/POS" });
    }
    return "SNP Printer Agent and printer are connected.";
  }
  if (config.type === "ble") await connectBle(config);
  else if (config.type === "serial") await connectSerial(config);
  else if (config.type === "usb") await connectUsb(config);
  else throw new Error(`Unsupported printer type: ${config.type}`);
  return `${config.type.toUpperCase()} printer is connected for this browser session.`;
};

export const listSilentWindowsPrinters = async (map) => {
  const agentPort = normalizeAgentPort(map?.silent_agent_port);
  await ensureAgent(agentPort);
  return agentRequest(agentPort, "/api/windows/printers");
};

export const inferSilentChannel = (name = "") => {
  const value = String(name).toLowerCase();
  if (value.includes("kot_item")) return "kot_item";
  if (value.includes("kot_previous")) return "kot_previous";
  if (value.includes("kot")) return "kot";
  if (value.includes("shop")) return "shop";
  return "user";
};

export const trySilentPrint = async ({ baseUrl, shopHash, billNo, channel, isMobile = false, silentData = {} }) => {
  if (isMobile || !baseUrl || !shopHash || !billNo) return false;
  try {
    const record = await loadConfig(baseUrl, shopHash);
    if (String(record.val || "").toLowerCase() !== "yes") return false;
    const config = channelConfig(record.map || {}, channel);
    if (!config.enabled) return false;
    const { data } = await axios.post(`${baseUrl}billing/print-bill-as-text`, {
      jcJson: {
        apiToken: CART_INITIAL_STATE.apiToken,
        shopHash,
        billNo,
        kot: channel.startsWith("kot"),
        shopPrint: channel === "shop",
        gen: false,
        isMobile: false,
        silentPrinting: true,
        printerChannel: channel,
        ...silentData,
      },
    });
    const text = data?.jcJson?.textToPrint;
    if (!text) throw new Error("The server did not return printable ESC/POS text.");
    const bytes = buildTaggedEscPos(text, config);
    for (let copy = 0; copy < config.copies; copy += 1) await sendEscPos(config.type, bytes, config);
    return true;
  } catch (error) {
    console.warn("Silent ESC/POS printing failed; using existing print fallback.", error);
    return false;
  }
};

